/**
 * Capability Pack Card Component
 *
 * Displays individual capability pack with:
 * - Name, description, agent count
 * - Estimated cost and duration
 * - Success rate indicators
 * - Included agents list (expandable)
 * - Launch workflow button
 *
 * Follows: .equilateral-standards/frontend-development/frontend_standards.md
 */

import React, { useState } from 'react';
import {
  CubeIcon,
  ClockIcon,
  CurrencyDollarIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  PlayIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import { Badge, Button, Card } from 'flowbite-react';

export interface CapabilityPack {
  id: string;
  name: string;
  description: string;
  category: string;
  agentCount: number;
  estimatedCost: number;
  estimatedDuration: number; // in milliseconds
  successRate: number;
  slashCommand: string;
  agents: {
    id: string;
    name: string;
    description: string;
  }[];
  features: string[];
  pricing?: {
    monthly: number;
    currency: string;
  };
}

interface CapabilityPackCardProps {
  pack: CapabilityPack;
  onLaunch?: (pack: CapabilityPack) => void;
  isExpanded?: boolean;
}

const CapabilityPackCard: React.FC<CapabilityPackCardProps> = ({
  pack,
  onLaunch,
  isExpanded: defaultExpanded = false
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  const formatDuration = (ms: number): string => {
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ${minutes % 60}m`;
  };

  const getCategoryColor = (category: string): string => {
    const colors: Record<string, string> = {
      security: 'failure',
      compliance: 'purple',
      privacy: 'indigo',
      deployment: 'warning',
      testing: 'success',
      business: 'pink',
      development: 'info',
      optimization: 'cyan',
      analysis: 'gray'
    };
    return colors[category.toLowerCase()] || 'gray';
  };

  const handleLaunch = () => {
    if (onLaunch) {
      onLaunch(pack);
    }
  };

  return (
    <Card className="hover:shadow-lg transition-shadow">
      <div className="space-y-4">
        {/* Header */}
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <SparklesIcon className="h-6 w-6 text-indigo-600" />
              <h3 className="text-xl font-bold text-gray-900">{pack.name}</h3>
            </div>
            <p className="text-sm text-gray-600">{pack.description}</p>
          </div>
          <Badge color={getCategoryColor(pack.category)} size="sm">
            {pack.category}
          </Badge>
        </div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-4 gap-4">
          {/* Agent Count */}
          <div className="flex items-center gap-2">
            <CubeIcon className="h-5 w-5 text-gray-500" />
            <div>
              <p className="text-xs text-gray-500">Agents</p>
              <p className="text-sm font-semibold">{pack.agentCount}</p>
            </div>
          </div>

          {/* Estimated Cost */}
          <div className="flex items-center gap-2">
            <CurrencyDollarIcon className="h-5 w-5 text-gray-500" />
            <div>
              <p className="text-xs text-gray-500">Est. Cost</p>
              <p className="text-sm font-semibold">${pack.estimatedCost.toFixed(2)}</p>
            </div>
          </div>

          {/* Duration */}
          <div className="flex items-center gap-2">
            <ClockIcon className="h-5 w-5 text-gray-500" />
            <div>
              <p className="text-xs text-gray-500">Duration</p>
              <p className="text-sm font-semibold">{formatDuration(pack.estimatedDuration)}</p>
            </div>
          </div>

          {/* Success Rate */}
          <div className="flex items-center gap-2">
            <CheckCircleIcon className="h-5 w-5 text-green-500" />
            <div>
              <p className="text-xs text-gray-500">Success</p>
              <p className="text-sm font-semibold">{(pack.successRate * 100).toFixed(0)}%</p>
            </div>
          </div>
        </div>

        {/* Success Rate Bar */}
        <div className="w-full bg-gray-200 rounded-full h-2.5">
          <div
            className="bg-green-600 h-2.5 rounded-full"
            style={{ width: `${pack.successRate * 100}%` }}
          />
        </div>

        {/* Pricing Badge */}
        {pack.pricing && (
          <div className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
            <span className="text-sm text-gray-600">Commercial Pack Pricing</span>
            <span className="text-lg font-bold text-gray-900">
              ${pack.pricing.monthly}/{pack.pricing.currency === 'USD' ? 'mo' : pack.pricing.currency}
            </span>
          </div>
        )}

        {/* Slash Command */}
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-indigo-600 font-medium">Slash Command</p>
              <code className="text-sm font-mono text-indigo-900">{pack.slashCommand}</code>
            </div>
            <Button
              size="sm"
              color="purple"
              onClick={handleLaunch}
            >
              <PlayIcon className="h-4 w-4 mr-1" />
              Launch
            </Button>
          </div>
        </div>

        {/* Expandable Agents List */}
        <div className="border-t border-gray-200 pt-4">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex items-center justify-between w-full text-left hover:bg-gray-50 p-2 rounded transition-colors"
          >
            <span className="text-sm font-medium text-gray-700">
              Included Agents ({pack.agents.length})
            </span>
            {isExpanded ? (
              <ChevronUpIcon className="h-5 w-5 text-gray-500" />
            ) : (
              <ChevronDownIcon className="h-5 w-5 text-gray-500" />
            )}
          </button>

          {isExpanded && (
            <div className="mt-3 space-y-2">
              {pack.agents.map((agent) => (
                <div
                  key={agent.id}
                  className="bg-gray-50 rounded-lg p-3 hover:bg-gray-100 transition-colors"
                >
                  <p className="text-sm font-medium text-gray-900">{agent.name}</p>
                  <p className="text-xs text-gray-600 mt-1">{agent.description}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Features List */}
        {pack.features && pack.features.length > 0 && (
          <div className="border-t border-gray-200 pt-4">
            <p className="text-sm font-medium text-gray-700 mb-2">Key Features</p>
            <ul className="space-y-1">
              {pack.features.slice(0, isExpanded ? undefined : 3).map((feature, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <CheckCircleIcon className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-gray-600">{feature}</span>
                </li>
              ))}
            </ul>
            {!isExpanded && pack.features.length > 3 && (
              <button
                onClick={() => setIsExpanded(true)}
                className="text-sm text-indigo-600 hover:text-indigo-700 mt-2"
              >
                +{pack.features.length - 3} more features
              </button>
            )}
          </div>
        )}
      </div>
    </Card>
  );
};

export default CapabilityPackCard;
